// Decorative motion for the standalone sign-in page. Everything here is
// optional polish: it never touches auth state and stays inert under reduced
// motion or in environments without pointer/animation support.

export type LoginMood = "idle" | "checking" | "sending" | "sent" | "warning" | "approved" | "entering";

export function setLoginMood(mood: LoginMood): void {
  if (typeof document === "undefined") return;
  document.body.dataset.loginMood = mood;
}

export function startLoginAmbience(): void {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
  const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

  bindStatusReplay();
  bindTypingPulse();
  if (reducedMotion.matches) return;
  bindPointerField();
}

// The stage, card and primary button read --px/--py (-1..1 across the
// viewport) and --mx/--my (pixels inside the hovered surface).
function bindPointerField() {
  const root = document.documentElement;
  const surfaces = Array.from(document.querySelectorAll<HTMLElement>("[data-spotlight]"));
  let frame = 0;
  let lastEvent: PointerEvent | null = null;

  const paint = () => {
    frame = 0;
    if (!lastEvent) return;
    const { clientX, clientY } = lastEvent;
    root.style.setProperty("--px", ((clientX / window.innerWidth) * 2 - 1).toFixed(3));
    root.style.setProperty("--py", ((clientY / window.innerHeight) * 2 - 1).toFixed(3));
    surfaces.forEach((surface) => {
      const rect = surface.getBoundingClientRect();
      surface.style.setProperty("--mx", `${clientX - rect.left}px`);
      surface.style.setProperty("--my", `${clientY - rect.top}px`);
    });
  };

  window.addEventListener("pointermove", (event) => {
    if (event.pointerType === "touch") return;
    lastEvent = event;
    if (!frame) frame = window.requestAnimationFrame(paint);
  }, { passive: true });
  document.documentElement.addEventListener("pointerleave", () => {
    root.style.setProperty("--px", "0");
    root.style.setProperty("--py", "0");
  });
}

function bindTypingPulse() {
  const field = document.querySelector<HTMLElement>(".field");
  const input = field?.querySelector<HTMLInputElement>("input");
  if (!field || !input) return;
  let timer = 0;
  input.addEventListener("input", () => {
    field.dataset.typing = "true";
    window.clearTimeout(timer);
    timer = window.setTimeout(() => { delete field.dataset.typing; }, 420);
  });
}

// Restart the status entrance whenever its message changes, not only when it
// first appears.
function bindStatusReplay() {
  const status = document.querySelector<HTMLElement>("#magic-status");
  if (!status || typeof MutationObserver === "undefined") return;
  let previous = "";
  new MutationObserver(() => {
    const current = `${status.dataset.kind ?? ""}|${status.textContent ?? ""}`;
    if (current === previous) return;
    previous = current;
    status.classList.remove("is-fresh");
    void status.offsetWidth;
    status.classList.add("is-fresh");
  }).observe(status, { attributes: true, attributeFilter: ["data-kind"], childList: true, characterData: true, subtree: true });
}
